import React, { forwardRef } from 'react';
import { styled } from '../../utils';
import { IconButton, IconButtonProps } from '../IconButton';
import { CollapsibleIcon } from './CollapsibleIcon';
import { CollapsibleIconBaseProps } from './model';
import { useCollapsibleA11yProps } from './useCollapsibleA11yProps';

export interface CollapsibleIconButtonProps extends CollapsibleIconBaseProps, Pick<IconButtonProps, 'onClick'> {
  /**
   * Set to true, to remove padding on the button. Should only be used in rare cases based on guidance from design (e.g.
   * this is used in the `Container` header with size set to `medium` to show a larger, more prominent icon while
   * minimizing the space taken up by the button).
   */
  noPadding?: boolean;
  /**
   * Size of the icon button.
   *
   * @default 'small'
   */
  size?: IconButtonProps['size'];
}

/**
 * Use the `CollapsibleIconButton` component when you are presenting the user with the ability to expand/collapse 
 * content vertically, the collapse icon is not part of a larger component that manages focus, clicking, and accessible
 * labels, and it makes sense to render as a standalone `IconButton`.
 * 
 * If the collapse icon is part of a larger component pattern, consider using `CollapsibleIcon` instead.

 * The `collapsed` behavior of this component is controlled (i.e. the consumer is responsible for managing the state).
 */
export const CollapsibleIconButton = forwardRef<HTMLButtonElement, CollapsibleIconButtonProps>(
  function CollapsibleIconButton({ collapsed, onClick, ariaLabelSubject, noPadding, size = 'small' }, ref) {
    const a11yProps = useCollapsibleA11yProps({ collapsed, ariaLabelSubject });

    return (
      <StyledIconButton ref={ref} size={size} onClick={onClick} noPadding={noPadding} {...a11yProps}>
        <CollapsibleIcon collapsed={collapsed} />
      </StyledIconButton>
    );
  }
);

const StyledIconButton = styled(IconButton, {
  // Need to remove props that IconButton does not know about or it causes React errors because it will get passed on
  // to the underlying dom node that doesn't know how to handle it.
  shouldForwardProp: (prop) => prop !== 'noPadding',
})<CollapsibleIconButtonProps>(({ noPadding }) => {
  return {
    padding: noPadding ? 0 : undefined,
  };
});
